import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon } from '@phosphor-icons/react';
import { Button, ChoiceGroup, LoadingOverlay } from '@skupreme/shared/ui';
import {
  FormCheckbox,
  FormNumberField,
  FormTextField,
} from '@skupreme/shared/ui/form';
import FpPartnersService from '@utility/services/fp-partners';
import { ToastMessage } from '@utility/toast';
import { customFieldsSchema } from '../../lib/custom-wms';
import type { WmsWarehouse } from './ProviderTypeStep';

type CustomWmsFormProps = {
  warehouse: WmsWarehouse;
  onConnected: (providerId: string | undefined) => void;
};

/** A partner WMS describes its own connection fields; this draws them. */
export function CustomWmsForm({ warehouse, onConnected }: CustomWmsFormProps) {
  const [loading, setLoading] = useState(false);
  const metadata = warehouse.integrationMetadata;
  const fields = useMemo(
    () => Object.entries(metadata?.customFields ?? {}),
    [metadata],
  );
  const { control, handleSubmit } = useForm<Record<string, unknown>>({
    resolver: zodResolver(customFieldsSchema(metadata?.customFields ?? {})),
  });
  const displayName = metadata?.displayName || warehouse.name;

  const connect = handleSubmit(async customFields => {
    try {
      setLoading(true);
      const result = await FpPartnersService.integrateWMSwarehouse({
        customFields,
        wms_organization_id: warehouse._id,
      });
      ToastMessage.success(`${displayName} Connected Successfully`);
      onConnected(result?.providers?.[0]);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="sk:relative sk:flex sk:flex-col sk:gap-4">
      {loading ? <LoadingOverlay /> : null}
      {metadata?.logo?.url ? (
        <img
          alt={displayName}
          src={metadata.logo.url}
          className="sk:mx-auto sk:max-h-25 sk:object-contain"
        />
      ) : (
        <h2 className="sk:m-0 sk:text-center sk:text-lg sk:font-medium">
          {displayName}
        </h2>
      )}
      {metadata?.description ? (
        <p className="sk:m-0 sk:text-center sk:text-dense sk:text-fg-secondary">
          {metadata.description}
        </p>
      ) : null}
      {fields.map(([name, field]) => {
        const common = {
          control,
          name,
          label: field.label,
          isRequired: !!field.required,
        };

        switch (field.type) {
          case 'text':
            return (
              <FormTextField
                key={name}
                {...common}
                placeholder={field.placeholder}
              />
            );
          case 'number':
            return (
              <FormNumberField
                key={name}
                {...common}
                placeholder={field.placeholder}
              />
            );
          case 'boolean':
            return <FormCheckbox key={name} {...common} />;
          case 'radio':
            return (
              <div key={name} className="sk:flex sk:flex-col sk:gap-1.5">
                <span className="sk:text-dense sk:font-medium sk:text-fg-secondary">
                  {field.label}
                </span>
                <Controller
                  control={control}
                  name={name}
                  render={({ field: bound, fieldState }) => (
                    <>
                      <ChoiceGroup
                        label={field.label}
                        options={field.options ?? []}
                        value={
                          typeof bound.value === 'string' ? bound.value : ''
                        }
                        onChange={bound.onChange}
                      />
                      {fieldState.error ? (
                        <span className="sk:text-xs sk:text-danger">
                          {fieldState.error.message}
                        </span>
                      ) : null}
                    </>
                  )}
                />
              </div>
            );
          default:
            return null;
        }
      })}
      {/* The wizard hides its own Create on this step, so this one stands in for it. */}
      <div className="sk:flex sk:justify-end">
        <Button variant="primary" onPress={() => connect()}>
          <CheckIcon size={12} /> Create
        </Button>
      </div>
    </div>
  );
}
